import React, { useState } from 'react'
import { Card, CardHeader } from '@/design-system/components/Card'
import { Button } from '@/design-system/components/Button'
import { Badge } from '@/design-system/components/Badge'
import { usePhotographers } from './hooks/useAdmin'
import type { AdminPhotographer, PhotographerFilters } from './hooks/useAdmin'

const STATUS_LABELS: Record<AdminPhotographer['status'], string> = {
  active: 'Aktiv',
  suspended: 'Suspenderad',
  pending: 'Väntar',
}

const STATUS_VARIANTS: Record<AdminPhotographer['status'], 'active' | 'urgent' | 'pending'> = {
  active: 'active',
  suspended: 'urgent',
  pending: 'pending',
}

const PAGE_SIZE = 25

export function PhotographerManagement() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<PhotographerFilters['status']>('all')
  const [page, setPage] = useState(1)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const filters: PhotographerFilters = { status: statusFilter, search: debouncedSearch }
  const { data, isLoading, suspend, activate } = usePhotographers(filters)

  // Simple debounce via timeout
  const handleSearchChange = (value: string) => {
    setSearch(value)
    clearTimeout((window as unknown as { _searchTimeout?: number })._searchTimeout)
    ;(window as unknown as { _searchTimeout?: number })._searchTimeout = window.setTimeout(() => {
      setDebouncedSearch(value)
      setPage(1)
    }, 300)
  }

  const totalPages = Math.ceil(data.length / PAGE_SIZE)
  const paged = data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handleStatusToggle = async (p: AdminPhotographer) => {
    if (p.status === 'active') {
      await suspend(p.user_id)
    } else {
      await activate(p.user_id)
    }
  }

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' })

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('sv-SE').format(v) + ' kr'

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Fotografer</h1>
        <p className="text-sm text-text-secondary mt-1">{data.length} fotografer totalt</p>
      </div>

      <Card>
        {/* Filters */}
        <CardHeader className="flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            type="text"
            placeholder="Sök namn eller e-post…"
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            className="flex-1 h-9 px-3 rounded-lg bg-surface-overlay border border-surface-border text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
          <div className="flex gap-2">
            {(['all', 'active', 'pending', 'suspended'] as const).map(s => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1) }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  statusFilter === s
                    ? 'bg-brand-accent text-white'
                    : 'bg-surface-overlay text-text-secondary hover:text-text-primary border border-surface-border'
                }`}
              >
                {s === 'all' ? 'Alla' : s === 'active' ? 'Aktiva' : s === 'pending' ? 'Väntande' : 'Suspenderade'}
              </button>
            ))}
          </div>
        </CardHeader>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border">
                {['Namn', 'E-post', 'Registrerad', 'Uppdrag', 'Intjänat', 'Status', ''].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j} className="px-5 py-3.5">
                        <div className="h-4 bg-surface-overlay rounded animate-pulse w-24" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-text-muted text-sm">
                    Inga Zoomers hittades
                  </td>
                </tr>
              ) : (
                paged.map(p => (
                  <tr key={p.id} className="hover:bg-surface-overlay/50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-brand-accent/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-brand-accent">
                            {p.display_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-text-primary">{p.display_name}</p>
                          {p.city && <p className="text-xs text-text-muted">{p.city}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-text-secondary">{p.email}</td>
                    <td className="px-5 py-3.5 text-text-secondary whitespace-nowrap">{formatDate(p.created_at)}</td>
                    <td className="px-5 py-3.5 text-text-primary font-medium tabular-nums">{p.total_missions}</td>
                    <td className="px-5 py-3.5 text-text-primary font-medium tabular-nums whitespace-nowrap">{formatCurrency(p.total_earned)}</td>
                    <td className="px-5 py-3.5">
                      <Badge variant={STATUS_VARIANTS[p.status]}>{STATUS_LABELS[p.status]}</Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2 justify-end">
                        <Button variant="ghost" size="sm">
                          Se profil
                        </Button>
                        <Button
                          variant={p.status === 'active' ? 'danger' : 'secondary'}
                          size="sm"
                          onClick={() => handleStatusToggle(p)}
                        >
                          {p.status === 'active' ? 'Suspenda' : 'Aktivera'}
                        </Button>
                        {p.total_earned > 0 && (
                          <Button variant="secondary" size="sm">
                            Utbetalning
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-surface-border">
            <p className="text-xs text-text-muted">
              Visar {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.length)} av {data.length}
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                ← Föregående
              </Button>
              <Button variant="ghost" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                Nästa →
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
